import React, { useState } from "react";
import NumericPopoverPicker from "./NumericPopoverPicker";
import NumpadPopover from "./NumpadPopover";
import { GameDefinition, GameOption } from "./gamesCatalog";
import { ActiveGame } from "./activeRound";
import {
  SKINS_OPTION_KEYS,
  TOURNAMENT_BET_GRID_IDS,
  TOURNAMENT_BET_GRID_KEYS,
  TOURNAMENT_GAMES,
  tournamentDefaultOptionsFor,
} from "./tournamentGames";

// Uncapped numeric options fall back to this ceiling so the picker has a
// finite range — matches the player-side modal.
const NUMBER_DEFAULT_MAX = 100;

type OptValue = boolean | number | string;
type Options = Record<string, OptValue>;

interface Props {
  games: ActiveGame[];
  onToggle: (id: string) => void;
  onOptionChange: (id: string, key: string, value: OptValue) => void;
}

function isOptionVisible(opt: GameOption, options: Options): boolean {
  if (!opt.dependsOn) return true;
  const actual = options[opt.dependsOn.key];
  const expected = opt.dependsOn.value;
  if (Array.isArray(expected)) return expected.includes(actual as never);
  return actual === expected;
}

// Tournament games step — same compact row layout as the player-side GamesModal
// (name + ⚙ options + ℹ info, both expanding inline) but driven by the
// tournament catalog and tournament-specific bet grids.
export default function TournamentGamesStep({
  games,
  onToggle,
  onOptionChange,
}: Props) {
  const [infoOpen, setInfoOpen] = useState<string | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState<string | null>(null);

  return (
    <div className="cg-wiz-pane">
      <p className="cg-subpage-note">
        Pick the games for the tournament — every group plays the same games.
        Use the ⚙ to set the terms (bets, format, options).
      </p>
      <ul className="cg-games-list">
        {TOURNAMENT_GAMES.map((game) => (
          <GameRow
            key={game.id}
            game={game}
            active={games.find((g) => g.id === game.id)}
            infoOpen={infoOpen === game.id}
            advancedOpen={advancedOpen === game.id}
            onToggle={() => onToggle(game.id)}
            onToggleInfo={() =>
              setInfoOpen((cur) => (cur === game.id ? null : game.id))
            }
            onToggleAdvanced={() =>
              setAdvancedOpen((cur) => (cur === game.id ? null : game.id))
            }
            onOptionChange={(key, value) =>
              onOptionChange(game.id, key, value)
            }
          />
        ))}
      </ul>
    </div>
  );
}

function GameRow({
  game,
  active,
  infoOpen,
  advancedOpen,
  onToggle,
  onToggleInfo,
  onToggleAdvanced,
  onOptionChange,
}: {
  game: GameDefinition;
  active?: ActiveGame;
  infoOpen: boolean;
  advancedOpen: boolean;
  onToggle: () => void;
  onToggleInfo: () => void;
  onToggleAdvanced: () => void;
  onOptionChange: (key: string, value: OptValue) => void;
}) {
  const isMandatory = !!game.mandatory;
  const enabled = isMandatory ? true : !!active?.enabled;
  const optionValues: Options =
    active?.options ?? tournamentDefaultOptionsFor(game.id);
  const hasOptions = game.options.length > 0;

  // Generic options = everything not owned by a custom grid, respecting
  // dependsOn visibility.
  const genericOpts = game.options.filter((o) => {
    if (TOURNAMENT_BET_GRID_IDS.has(game.id) && TOURNAMENT_BET_GRID_KEYS.has(o.key))
      return false;
    if (game.id === "skins" && SKINS_OPTION_KEYS.has(o.key)) return false;
    return isOptionVisible(o, optionValues);
  });

  const showBetGrid =
    TOURNAMENT_BET_GRID_IDS.has(game.id) &&
    game.options.some((o) => o.key === "overallBet");

  return (
    <li className={`cg-games-row${enabled ? " cg-games-row-on" : ""}`}>
      <div className="cg-games-row-head">
        <label className="cg-games-toggle-label">
          <input
            type="checkbox"
            className="cg-games-toggle"
            checked={enabled}
            disabled={isMandatory}
            onChange={onToggle}
          />
          <span className="cg-games-name">{game.name}</span>
        </label>
        {enabled && hasOptions && (
          <button
            type="button"
            className={`cg-games-options-btn${advancedOpen ? " cg-games-options-on" : ""}`}
            onClick={onToggleAdvanced}
            aria-label={`Options for ${game.name}`}
            aria-expanded={advancedOpen}
            title="Set the terms"
          >
            ⚙
          </button>
        )}
        <button
          type="button"
          className={`cg-games-info-btn${infoOpen ? " cg-games-info-on" : ""}`}
          onClick={onToggleInfo}
          aria-label={`About ${game.name}`}
          aria-expanded={infoOpen}
          title="Rules"
        >
          i
        </button>
      </div>

      {infoOpen && <p className="cg-games-desc">{game.description}</p>}

      {enabled && advancedOpen && hasOptions && (
        <div className="cg-games-advanced">
          {showBetGrid && (
            <TournamentBetGrid
              gameId={game.id}
              nassau={optionValues.nassau === true}
              overallBet={Number(optionValues.overallBet) || 0}
              betPerPoint={Number(optionValues.betPerPoint) || 0}
              frontNineBet={Number(optionValues.frontNineBet) || 0}
              backNineBet={Number(optionValues.backNineBet) || 0}
              useBetPerPoint={optionValues.useBetPerPoint === true}
              onChange={onOptionChange}
            />
          )}
          {game.id === "skins" && (
            <SkinsBetGrid values={optionValues} onChange={onOptionChange} />
          )}
          {genericOpts.map((opt) => (
            <GameOptionField
              key={opt.key}
              option={opt}
              value={optionValues[opt.key]}
              onChange={(v) => onOptionChange(opt.key, v)}
            />
          ))}
        </div>
      )}
    </li>
  );
}

// ─── Single option control (boolean / select / number) ─────────────────────

function GameOptionField({
  option,
  value,
  onChange,
}: {
  option: GameOption;
  value: OptValue | undefined;
  onChange: (next: OptValue) => void;
}) {
  const v = value ?? option.default;
  const [infoOpen, setInfoOpen] = useState(false);

  if (option.type === "boolean") {
    return (
      <div className="cg-games-opt-bool-row">
        <label className="cg-games-opt cg-games-opt-bool">
          <input
            type="checkbox"
            checked={!!v}
            onChange={(e) => onChange(e.target.checked)}
          />
          <span>{option.label}</span>
        </label>
        {option.info && (
          <button
            type="button"
            className={`cg-games-info-btn${infoOpen ? " cg-games-info-on" : ""}`}
            onClick={() => setInfoOpen((o) => !o)}
            aria-label={`About ${option.label}`}
            aria-expanded={infoOpen}
            title="Info"
          >
            i
          </button>
        )}
        {option.info && infoOpen && (
          <p className="cg-games-desc cg-opt-info-desc">{option.info}</p>
        )}
      </div>
    );
  }

  if (option.type === "select") {
    if (option.renderAs === "radio") {
      return (
        <div className="cg-games-opt cg-games-opt-handicap-radio">
          {(option.choices ?? []).map((c) => (
            <label key={c.value} className="cg-handicap-radio-option">
              <input
                type="radio"
                checked={String(v) === c.value}
                onChange={() => onChange(c.value)}
              />
              <span>{c.label}</span>
            </label>
          ))}
        </div>
      );
    }
    return (
      <label className="cg-games-opt cg-games-opt-select">
        <span className="cg-games-opt-label">{option.label}</span>
        <select
          className="cg-games-opt-dropdown"
          value={String(v)}
          onChange={(e) => onChange(e.target.value)}
        >
          {(option.choices ?? []).map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      </label>
    );
  }

  const min = option.min ?? 0;
  const max = option.max ?? NUMBER_DEFAULT_MAX;
  const step = option.step ?? 1;
  const numeric = typeof value === "number" ? value : Number(value) || 0;

  return (
    <label className="cg-games-opt cg-games-opt-num">
      <span className="cg-games-opt-label">{option.label}</span>
      <NumericPopoverPicker
        value={numeric}
        onChange={(next) => onChange(next)}
        min={min}
        max={max}
        step={step}
        values={option.values}
        zeroLabel={option.zeroLabel}
        ariaLabel={option.label}
      />
    </label>
  );
}

// ─── Bet grid (Bet per Player / Bet per Point + Nassau) ────────────────────
// Same three-column layout the player side uses, kept self-contained here so
// the tournament editor doesn't depend on the modal internals.
function TournamentBetGrid({
  gameId,
  nassau,
  overallBet,
  betPerPoint,
  frontNineBet,
  backNineBet,
  useBetPerPoint,
  onChange,
}: {
  gameId: string;
  nassau: boolean;
  overallBet: number;
  betPerPoint: number;
  frontNineBet: number;
  backNineBet: number;
  useBetPerPoint: boolean;
  onChange: (key: string, next: OptValue) => void;
}) {
  if (nassau) {
    return (
      <div className="cg-stableford-bet-grid">
        <div className="cg-bet-grid-cell">
          <span className="cg-bet-grid-title">Front 9 bet</span>
          <span className="cg-bet-grid-controls">
            <NumericPopoverPicker value={frontNineBet} onChange={(v) => onChange("frontNineBet", v)} min={0} max={100} step={1} zeroLabel="—" ariaLabel="Front 9 bet" />
          </span>
        </div>
        <div className="cg-bet-grid-cell">
          <span className="cg-bet-grid-title">Back 9 bet</span>
          <span className="cg-bet-grid-controls">
            <NumericPopoverPicker value={backNineBet} onChange={(v) => onChange("backNineBet", v)} min={0} max={100} step={1} zeroLabel="—" ariaLabel="Back 9 bet" />
          </span>
        </div>
        <div className="cg-bet-grid-cell">
          <span className="cg-bet-grid-title">Overall bet</span>
          <span className="cg-bet-grid-controls">
            <NumericPopoverPicker value={overallBet} onChange={(v) => onChange("overallBet", v)} min={0} max={100} step={1} zeroLabel="—" ariaLabel="Overall bet" />
          </span>
        </div>
      </div>
    );
  }
  return (
    <div className="cg-stableford-bet-grid">
      <label className="cg-bet-grid-cell">
        <span className="cg-bet-grid-title">Bet per Point</span>
        <span className="cg-bet-grid-controls">
          <input
            type="radio"
            name={`${gameId}-bet-mode`}
            checked={useBetPerPoint}
            onChange={() => onChange("useBetPerPoint", true)}
          />
          <span className={`cg-bet-input-wrap${!useBetPerPoint ? " cg-bet-input-disabled" : ""}`}>
            <NumericPopoverPicker value={betPerPoint} onChange={(v) => onChange("betPerPoint", v)} min={0} max={100} step={1} zeroLabel="—" ariaLabel="Bet per Point" />
          </span>
        </span>
      </label>
      <div className="cg-bet-grid-cell cg-bet-grid-cell-spacer" aria-hidden />
      <label className="cg-bet-grid-cell">
        <span className="cg-bet-grid-title">Bet per Player</span>
        <span className="cg-bet-grid-controls">
          <input
            type="radio"
            name={`${gameId}-bet-mode`}
            checked={!useBetPerPoint}
            onChange={() => onChange("useBetPerPoint", false)}
          />
          <span className={`cg-bet-input-wrap${useBetPerPoint ? " cg-bet-input-disabled" : ""}`}>
            <NumericPopoverPicker value={overallBet} onChange={(v) => onChange("overallBet", v)} min={0} max={100} step={1} zeroLabel="—" ariaLabel="Bet per Player" />
          </span>
        </span>
      </label>
    </div>
  );
}

// ─── Skins bet grid (Pot / Carry forward, with per-player vs total pot) ────
function SkinsBetGrid({
  values,
  onChange,
}: {
  values: Options;
  onChange: (key: string, next: OptValue) => void;
}) {
  const mode = String(values.mode ?? "pot");
  const potMode = String(values.potMode ?? "perPlayer");

  return (
    <div className="cg-skins-grid">
      {/* Pot vs Carry forward */}
      <div className="cg-games-opt cg-games-opt-handicap-radio">
        <label className="cg-handicap-radio-option">
          <input type="radio" name="skins-mode" checked={mode === "pot"} onChange={() => onChange("mode", "pot")} />
          <span>Pot</span>
        </label>
        <label className="cg-handicap-radio-option">
          <input type="radio" name="skins-mode" checked={mode === "carryForward"} onChange={() => onChange("mode", "carryForward")} />
          <span>Carry forward</span>
        </label>
      </div>

      {mode === "pot" && (
        <div className="cg-skins-pot">
          <div className="cg-skins-pot-head">
            <span />
            <span>Gross</span>
            <span>Net</span>
          </div>

          <label className="cg-skins-pot-row">
            <span className="cg-skins-pot-radio">
              <input type="radio" name="skins-pot-mode" checked={potMode === "perPlayer"} onChange={() => onChange("potMode", "perPlayer")} />
              <span>Bet per player</span>
            </span>
            <span className={`cg-bet-input-wrap${potMode !== "perPlayer" ? " cg-bet-input-disabled" : ""}`}>
              <NumericPopoverPicker value={Number(values.potBetGross) || 0} onChange={(v) => onChange("potBetGross", v)} min={0} max={100} step={1} zeroLabel="—" ariaLabel="Player bet gross" />
            </span>
            <span className={`cg-bet-input-wrap${potMode !== "perPlayer" ? " cg-bet-input-disabled" : ""}`}>
              <NumericPopoverPicker value={Number(values.potBetNet) || 0} onChange={(v) => onChange("potBetNet", v)} min={0} max={100} step={1} zeroLabel="—" ariaLabel="Player bet net" />
            </span>
          </label>

          <label className="cg-skins-pot-row">
            <span className="cg-skins-pot-radio">
              <input type="radio" name="skins-pot-mode" checked={potMode === "totalPot"} onChange={() => onChange("potMode", "totalPot")} />
              <span>Total pot</span>
            </span>
            <span className={`cg-bet-input-wrap${potMode !== "totalPot" ? " cg-bet-input-disabled" : ""}`}>
              <NumpadPopover value={Number(values.potTotalGross) || undefined} onChange={(v) => onChange("potTotalGross", v ?? 0)} min={0} maxDecimals={2} maxLength={8} placeholder="—" ariaLabel="Total pot gross" />
            </span>
            <span className={`cg-bet-input-wrap${potMode !== "totalPot" ? " cg-bet-input-disabled" : ""}`}>
              <NumpadPopover value={Number(values.potTotalNet) || undefined} onChange={(v) => onChange("potTotalNet", v ?? 0)} min={0} maxDecimals={2} maxLength={8} placeholder="—" ariaLabel="Total pot net" />
            </span>
          </label>
        </div>
      )}

      {mode === "carryForward" && (
        <div className="cg-skins-pot">
          <div className="cg-skins-pot-head">
            <span />
            <span>Gross</span>
            <span>Net</span>
          </div>
          <div className="cg-skins-pot-row">
            <span className="cg-skins-pot-radio">
              <span>Bet per hole</span>
            </span>
            <span className="cg-bet-input-wrap">
              <NumericPopoverPicker value={Number(values.carryBetGross) || 0} onChange={(v) => onChange("carryBetGross", v)} min={0} max={100} step={1} zeroLabel="—" ariaLabel="Carry bet gross" />
            </span>
            <span className="cg-bet-input-wrap">
              <NumericPopoverPicker value={Number(values.carryBetNet) || 0} onChange={(v) => onChange("carryBetNet", v)} min={0} max={100} step={1} zeroLabel="—" ariaLabel="Carry bet net" />
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
